import React, { useEffect, useState, useRef } from 'react';
import TopBar from './components/TopBar';
import StarfieldBackground from './components/StarfieldBackground';
import TestimonialCarousel from './components/TestimonialCarousel';
import PainSection from './components/PainSection';
import ThreeStepRecipe from './components/ThreeStepRecipe';
import ResultsSection from './components/ResultsSection';
import PricingSection from './components/PricingSection';
import FastTrackYear from './components/FastTrackYear';
import StarsAndBeyond from './components/StarsAndBeyond';
import FinalCTA from './components/FinalCTA';

import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';

export default function App() {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white font-sans relative overflow-hidden">
      <StarfieldBackground />
      <TopBar />
      
      <div className="pt-32 px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
        {/* Video / 3D Viewer Section - Now at the top */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-4 md:p-6 shadow-2xl border border-gray-700/50 mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-600 mb-4">
            Watch How It Works
          </h2>
          <div className="relative w-full mx-auto" style={{ paddingBottom: '56.25%' /* 16:9 Aspect Ratio */ }}>
            {/* Neon glow border for the video iframe, positioned behind it */}
            <motion.div
              className="absolute inset-0 rounded-xl z-0" // Positioned behind the iframe
              style={{
                background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', // Pearl white gradient
                filter: 'blur(8px)', // Soft blur for glow effect
              }}
              animate={{ opacity: [0.2, 0.6, 0.2] }} // Slow flicker
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-xl z-10" // Iframe on top
              src="https://player.vimeo.com/video/1010780473?h=f06840a10e"
              title="Explainer Video"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              allowFullScreen
            ></iframe>
          </div>
          {/* New text added here */}
          <p className="text-white text-lg md:text-xl font-semibold mt-6 mb-4">
            We don't make promises. We just deliver Results!
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              onClick={() => setShowModal(true)}
            >
              <span>Schedule a Consultation</span>
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </motion.div>

        <PainSection />
        <ThreeStepRecipe />
        <TestimonialCarousel />
        <ResultsSection />
        <PricingSection />
        <FastTrackYear />
        <StarsAndBeyond />
        <FinalCTA />
      </div>

      {/* Modal para el formulario de consulta */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            {/* Fondo oscuro con blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-md"
              onClick={() => setShowModal(false)}
            />
            
            {/* Contenedor del modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 20 }}
              className="relative bg-gradient-to-br from-gray-900 to-blue-950 rounded-xl shadow-2xl border border-blue-700/30 w-full max-w-2xl"
            >
              {/* Header con título y botón de cerrar - Centrado perfecto */}
              <div className="relative flex items-center p-4 bg-blue-900/50 rounded-t-xl border-b border-blue-600/30">
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-1 rounded-full hover:bg-blue-800 transition-colors flex-shrink-0"
                    aria-label="Close"
                  >
                    <X size={20} className="text-white" />
                  </button>
                </div>
                <div className="w-full text-center">
                  <h3 className="text-xl font-bold text-white">Schedule a Consultation</h3>
                </div>
              </div>
              
              {/* Contenido del modal */}
              <div className="p-4">
                <div className="w-full h-[500px]">
                  <iframe
                    src="https://api.leadconnectorhq.com/widget/form/KkXYIE5MeSJOUUHG0Mou"
                    className="w-full h-full rounded-lg"
                    frameBorder="0"
                    title="Consultation Form"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}